// src/admin/AdminDashboard.js
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../firebase/AuthContext";
import {
  getAbout, saveAbout, getPrograms, addProgram, updateProgram, deleteProgram,
  getTeam, addTeamMember, updateTeamMember, deleteTeamMember,
  getBoard, addBoardMember, updateBoardMember, deleteBoardMember,
  getStories, addStory, updateStory, deleteStory,
  getJobs, addJob, updateJob, deleteJob,
  getAnnouncements, addAnnouncement, updateAnnouncement, deleteAnnouncement,
  getVideos, saveVideos, uploadImage
} from "../firebase/firestore";

// Image Upload Field
function ImageField({ label, value, folder, onChange }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const { url } = await uploadImage(file, folder); onChange(url); }
    catch { alert("Upload failed. Check Firebase Storage rules."); }
    finally { setUploading(false); }
  };
  return (
    <div className="admin-field">
      <label>{label}</label>
      <div style={{ display:"flex", gap:"0.75rem", alignItems:"center", flexWrap:"wrap" }}>
        {value && <img src={value} alt="preview" className="admin-img-preview" />}
        <div style={{ flex:1 }}>
          <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder="Paste image URL or upload" style={{ marginBottom:"0.4rem" }} />
          <input type="file" accept="image/*" ref={ref} style={{ display:"none" }} onChange={handleFile} />
          <button type="button" className="admin-btn admin-btn-outline" onClick={()=>ref.current.click()} style={{ fontSize:"0.78rem" }}>
            {uploading ? "Uploading..." : "📁 Upload Image"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Save Banner
function SaveBanner({ show, onSave, saving }) {
  if (!show) return null;
  return (
    <div className="admin-save-banner">
      <span>You have unsaved changes</span>
      <button className="admin-btn admin-btn-primary" onClick={onSave} disabled={saving}>{saving ? "Saving..." : "💾 Save Changes"}</button>
    </div>
  );
}

// Modal
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal"><div className="modal-title">{title}</div>{children}</div>
    </div>
  );
}

// About Editor
function AboutEditor() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getAbout().then(a=>setData(a||{})); }, []);
  const set = (k,v) => { setData(p=>({...p,[k]:v})); setDirty(true); };
  const save = async () => { setSaving(true); await saveAbout(data); setSaving(false); setDirty(false); };
  if (!data) return <div className="loading">Loading...</div>;
  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-title">About Section</div>
        <div className="admin-field"><label>Main Description</label><textarea value={data.description||""} onChange={e=>set("description",e.target.value)} style={{minHeight:120}} /></div>
        <div className="admin-field"><label>Second Paragraph</label><textarea value={data.description2||""} onChange={e=>set("description2",e.target.value)} style={{minHeight:120}} /></div>
        <ImageField label="About Image" value={data.imageUrl} folder="about" onChange={v=>set("imageUrl",v)} />
      </div>
      <SaveBanner show={dirty} onSave={save} saving={saving} />
    </div>
  );
}

// Videos Editor
function VideosEditor() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getVideos().then(v=>setData(v||{video1:"https://youtu.be/esLmYuogO3s",video2:"https://youtu.be/RIIIYipZyik"})); }, []);
  const set = (k,v) => { setData(p=>({...p,[k]:v})); setDirty(true); };
  const save = async () => { setSaving(true); await saveVideos(data); setSaving(false); setDirty(false); };
  if (!data) return <div className="loading">Loading...</div>;
  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-title">YouTube Videos</div>
        <div className="admin-field"><label>Video 1 — Our Work (YouTube URL)</label><input value={data.video1||""} onChange={e=>set("video1",e.target.value)} placeholder="https://youtu.be/..." /></div>
        <div className="admin-field"><label>Video 1 Title</label><input value={data.video1Title||""} onChange={e=>set("video1Title",e.target.value)} placeholder="Our Work in the Community" /></div>
        <div className="admin-field"><label>Video 2 — Beneficiary Story (YouTube URL)</label><input value={data.video2||""} onChange={e=>set("video2",e.target.value)} placeholder="https://youtu.be/..." /></div>
        <div className="admin-field"><label>Video 2 Title</label><input value={data.video2Title||""} onChange={e=>set("video2Title",e.target.value)} placeholder="A Beneficiary's Story" /></div>
      </div>
      <SaveBanner show={dirty} onSave={save} saving={saving} />
    </div>
  );
}

// Generic Collection Manager
function CollectionManager({ label, fields, fetchFn, addFn, updateFn, deleteFn, imageFolder }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => fetchFn().then(d=>{setItems(d);setLoading(false);});
  useEffect(()=>{load();},[]);
  const openAdd = () => setModal({mode:"add",data:{}});
  const openEdit = (item) => setModal({mode:"edit",data:{...item}});
  const closeModal = () => setModal(null);
  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode==="add") await addFn(modal.data);
      else await updateFn(modal.data.id,modal.data);
      await load(); closeModal();
    } catch(err){alert("Save failed: "+err.message);}
    setSaving(false);
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await deleteFn(id); setItems(prev=>prev.filter(i=>i.id!==id));
  };
  const setField = (key,val) => setModal(m=>({...m,data:{...m.data,[key]:val}}));
  if (loading) return <div className="loading">Loading...</div>;
  return (
    <div>
      <div className="admin-card">
        <div className="admin-card-title">
          {label} <span style={{color:"#64748B",fontSize:"0.8rem",fontWeight:400}}>({items.length} items)</span>
          <button className="admin-btn admin-btn-primary" onClick={openAdd}>+ Add</button>
        </div>
        {!items.length
          ? <div className="empty-state"><p>No {label.toLowerCase()} yet. Click Add to create one.</p></div>
          : items.map(item=>(
            <div className="admin-list-item" key={item.id}>
              {(item.imageUrl||item.photoUrl) && <img src={item.imageUrl||item.photoUrl} alt="" className="admin-img-preview" />}
              <div className="admin-list-item-info" style={{flex:1}}>
                <h4>{item.title||item.name||item.text||item.quote?.substring(0,60)+"..."||"—"}</h4>
                <p>{item.description?.substring(0,80)||item.role||item.type||item.department||""}</p>
                {item.status && <span className={`admin-status ${item.status==="open"?"status-active":"status-closed"}`}>{item.status}</span>}
              </div>
              <div className="admin-list-actions">
                <button className="admin-btn admin-btn-outline" onClick={()=>openEdit(item)}>Edit</button>
                <button className="admin-btn admin-btn-danger" onClick={()=>handleDelete(item.id)}>Delete</button>
              </div>
            </div>
          ))
        }
      </div>
      {modal && (
        <Modal title={`${modal.mode==="add"?"Add":"Edit"} ${label.slice(0,-1)}`} onClose={closeModal}>
          {fields.map(f=>{
            if (f.type==="image") return <ImageField key={f.key} label={f.label} value={modal.data[f.key]} folder={imageFolder||"uploads"} onChange={v=>setField(f.key,v)} />;
            if (f.type==="select") return (
              <div className="admin-field" key={f.key}><label>{f.label}</label>
                <select value={modal.data[f.key]||""} onChange={e=>setField(f.key,e.target.value)}>
                  {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            );
            if (f.type==="textarea") return (
              <div className="admin-field" key={f.key}><label>{f.label}</label>
                <textarea value={modal.data[f.key]||""} onChange={e=>setField(f.key,e.target.value)} placeholder={f.placeholder} style={{minHeight:f.tall?140:80}} />
              </div>
            );
            if (f.type==="toggle") return (
              <div className="admin-field" key={f.key} style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                <input type="checkbox" id={f.key} checked={!!modal.data[f.key]} onChange={e=>setField(f.key,e.target.checked)} style={{width:"auto"}} />
                <label htmlFor={f.key} style={{margin:0,textTransform:"none",letterSpacing:0}}>{f.label}</label>
              </div>
            );
            return (
              <div className="admin-field" key={f.key}><label>{f.label}</label>
                <input type={f.type||"text"} value={modal.data[f.key]||""} onChange={e=>setField(f.key,e.target.value)} placeholder={f.placeholder} />
              </div>
            );
          })}
          <div className="modal-actions">
            <button className="admin-btn admin-btn-outline" onClick={closeModal}>Cancel</button>
            <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>{saving?"Saving...":"Save"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const SECTIONS = [
  { key:"about", label:"About", icon:"💜", component:AboutEditor },
  { key:"programs", label:"Programs", icon:"📋", component:()=><CollectionManager label="Programs" imageFolder="programs" fetchFn={getPrograms} addFn={addProgram} updateFn={updateProgram} deleteFn={deleteProgram} fields={[{key:"title",label:"Title",placeholder:"Education Support"},{key:"category",label:"Category",placeholder:"Education"},{key:"description",label:"Description",type:"textarea"},{key:"imageUrl",label:"Image",type:"image"}]} /> },
  { key:"team", label:"Team", icon:"👥", component:()=><CollectionManager label="Team Members" imageFolder="team" fetchFn={getTeam} addFn={addTeamMember} updateFn={updateTeamMember} deleteFn={deleteTeamMember} fields={[{key:"name",label:"Full Name",placeholder:"Nakimbugwe Joan"},{key:"role",label:"Role",placeholder:"Executive Director"},{key:"bio",label:"Bio",type:"textarea"},{key:"photoUrl",label:"Photo",type:"image"}]} /> },
  { key:"board", label:"Board", icon:"🏛️", component:()=><CollectionManager label="Board Members" imageFolder="board" fetchFn={getBoard} addFn={addBoardMember} updateFn={updateBoardMember} deleteFn={deleteBoardMember} fields={[{key:"name",label:"Full Name",placeholder:"Ms. Gladys Soi"},{key:"role",label:"Role",placeholder:"Board Chair"},{key:"bio",label:"Bio",type:"textarea"},{key:"photoUrl",label:"Photo",type:"image"}]} /> },
  { key:"stories", label:"Stories", icon:"⭐", component:()=><CollectionManager label="Success Stories" imageFolder="stories" fetchFn={getStories} addFn={addStory} updateFn={updateStory} deleteFn={deleteStory} fields={[{key:"highlight",label:"Story Title",placeholder:"From Setback to Self-Reliance"},{key:"quote",label:"Story",type:"textarea",tall:true},{key:"name",label:"Name",placeholder:"Agnes"},{key:"location",label:"Location",placeholder:"Kampala, Uganda"},{key:"badge",label:"Badge",placeholder:"Catering Graduate"},{key:"photoUrl",label:"Photo",type:"image"},{key:"videoUrl",label:"YouTube Video URL",placeholder:"https://youtu.be/..."}]} /> },
  { key:"videos", label:"Videos", icon:"🎥", component:VideosEditor },
  { key:"jobs", label:"Jobs", icon:"💼", component:()=><CollectionManager label="Jobs" fetchFn={getJobs} addFn={addJob} updateFn={updateJob} deleteFn={deleteJob} fields={[{key:"title",label:"Job Title",placeholder:"Field Officer"},{key:"department",label:"Department",placeholder:"Programs"},{key:"location",label:"Location",placeholder:"Kampala, Uganda"},{key:"type",label:"Type",type:"select",options:["Full-time","Part-time","Contract","Volunteer","Internship"]},{key:"status",label:"Status",type:"select",options:["open","closed"]},{key:"description",label:"Description",type:"textarea",tall:true},{key:"requirements",label:"Requirements",type:"textarea",tall:true}]} /> },
  { key:"announcements", label:"Announcements", icon:"📢", component:()=><CollectionManager label="Announcements" fetchFn={getAnnouncements} addFn={addAnnouncement} updateFn={updateAnnouncement} deleteFn={deleteAnnouncement} fields={[{key:"text",label:"Announcement Text",placeholder:"Applications now open..."},{key:"cta",label:"CTA Text",placeholder:"Apply Now →"},{key:"active",label:"Show on website",type:"toggle"}]} /> },
];

export default function AdminDashboard() {
  const [active, setActive] = useState("about");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/admin/login"); };
  const ActiveComponent = SECTIONS.find(s=>s.key===active)?.component || AboutEditor;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <img src="/logo-light.png" alt="Mother Child Empowerment Initiative" />
          <p>Content Management System</p>
        </div>
        {SECTIONS.map(s=>(
          <button key={s.key} className={`admin-nav-item ${active===s.key?"active":""}`} onClick={()=>setActive(s.key)}>
            <span style={{fontSize:"15px"}}>{s.icon}</span> {s.label}
          </button>
        ))}
        <div style={{marginTop:"auto",padding:"1rem 0",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
          <div style={{padding:"0.5rem 1.5rem",fontSize:"0.75rem",color:"rgba(255,255,255,0.3)",marginBottom:"0.25rem"}}>
            Signed in as<br/><span style={{color:"rgba(255,255,255,0.6)"}}>{user?.email}</span>
          </div>
          <button className="admin-nav-item" onClick={handleLogout} style={{color:"#F87171"}}>🚪 Sign Out</button>
        </div>
      </aside>
      <main className="admin-main">
        <div className="admin-topbar">
          <div>
            <h1>{SECTIONS.find(s=>s.key===active)?.label}</h1>
            <p style={{fontSize:"0.82rem",color:"#64748B",marginTop:"0.2rem"}}>Changes appear on the live website instantly after saving.</p>
          </div>
          <a href="/" target="_blank" className="admin-btn admin-btn-outline" style={{textDecoration:"none"}}>🌐 View Website</a>
        </div>
        <ActiveComponent />
      </main>
    </div>
  );
}
